// How to play Bad Moon Rising: the second game. It stands alone, but is written for a table that
// has played Trouble Brewing.
import type { ScriptLoad } from "../../hooks/useScript";
import {
  CharacterSection,
  DaySteps,
  EveryoneTips,
  NightBasics,
  ScriptCharacters,
  Section,
  SetupSection,
  WhoIsExecuted,
  type GuideSection,
} from "./shared";

export const BAD_MOON_RISING_SECTIONS: GuideSection[] = [
  { id: "setup", label: "Setup" },
  { id: "character", label: "Your character" },
  { id: "night", label: "Night" },
  { id: "day", label: "Day" },
  { id: "dying", label: "Dying" },
  { id: "clues", label: "Drunk and poisoned" },
  { id: "winning", label: "Winning" },
  { id: "tips", label: "Tips" },
  { id: "characters", label: "Characters" },
];

export function BadMoonRisingIntro() {
  return (
    <section className="card stack">
      <h3>The short version</h3>
      <ul>
        <li>
          Everyone secretly gets a character. Most players are{" "}
          <strong className="good">good</strong>. A few are <strong className="evil">evil</strong>,
          and one of them is the <strong className="evil">Demon</strong>.
        </li>
        <li>
          Each <strong>night</strong>, the Demon kills, and often more than one player dies. Some
          good players keep others alive, and some dead players come back.
        </li>
        <li>
          Each <strong>day</strong>, everyone talks it over and can vote to execute one player. Some
          players survive their execution.
        </li>
        <li>
          <strong className="good">Good wins</strong> when the Demon dies.{" "}
          <strong className="evil">Evil wins</strong> when only two players are left alive.
        </li>
      </ul>
      <p className="small muted">
        Good players get fewer clues than in Trouble Brewing. Most of what you learn comes from who
        dies each night, how many, and who survives when they shouldn&apos;t. If this is your first
        game, play Trouble Brewing first.
      </p>
    </section>
  );
}

export function BadMoonRisingGuide({ script }: { script: ScriptLoad }) {
  return (
    <>
      <SetupSection gameName="Bad Moon Rising" />
      <CharacterSection />

      <Section id="night" title="Night">
        <NightBasics
          extra={
            <>
              <li>
                Some characters choose a character as well as, or instead of, a player: the
                Courtier, and the Gambler&apos;s guess. Tap a name in the list, then{" "}
                <strong>Confirm</strong>.
              </li>
              <li>
                Some abilities work once per game: the Courtier&apos;s, the Professor&apos;s and the
                Assassin&apos;s. Tap <strong>Nobody</strong> to keep yours for another night.
              </li>
              <li>
                Your town square shows who was alive at dusk: a death or a return to life in the
                night only shows at dawn. If your character must choose a living player (the
                Sailor, the Chambermaid, the Devil&apos;s Advocate) or a dead one (the Professor),
                and the player you choose has died or come back tonight, the Storyteller shakes
                their head. Your screen says so, and you choose again. Nobody else sees it.
              </li>
            </>
          }
          timeouts={
            <li>
              A few choices are skipped instead: a once-per-game ability is not used, and the
              Gambler makes no guess, so does not die. The Po may choose nobody, but if time runs
              out the app chooses for them: one player, or three if they chose nobody last time.
            </li>
          }
        />
        <div className="card card-tight stack-sm">
          <strong>The first night is different</strong>
          <p className="small">
            Nobody dies, though the Pukka already poisons someone. With 7 or more players, the evil
            players learn who their teammates are, and the Demon is shown three good characters that
            are not in the game. Those are safe to pretend to be. In a smaller game, evil players
            have to find each other.
          </p>
        </div>
      </Section>

      <Section id="day" title="Day">
        <DaySteps />
        <WhoIsExecuted />
        <div className="card stack-sm">
          <h3>What is different by day</h3>
          <ul>
            <li>
              <strong>Looking dead is not being dead.</strong> One Demon, the Zombuul, can look
              dead while it is still alive. The dead can be nominated, so the town can still
              execute it.
            </li>
            <li>
              <strong>Some executed players don&apos;t die.</strong> The day screen says so, but
              never why: it might be the Pacifist, the Devil&apos;s Advocate, the Tea Lady, the
              Sailor or the Fool.
            </li>
            <li>
              <strong>Statements.</strong> Once a day, anyone may tap{" "}
              <strong>Make a statement</strong> and pick a sentence such as &quot;Ann is evil&quot;,
              &quot;Bob is not the Gossip&quot;, &quot;Ann and Bob are on the same team&quot; or
              &quot;The Tinker is in play&quot;. Say it out loud too: every phone shows it. If the
              Gossip makes one and it is true, a player dies that night. Anyone can pretend to be
              the Gossip, but only the real one&apos;s statement kills.
            </li>
            <li>
              <strong>Someone can die in the middle of the day.</strong> The Tinker might die at any
              moment. Every phone shows it at once, though never during a vote.
            </li>
            <li>
              <strong>Choosing as you die.</strong> A player who has just died may choose a living
              player, out loud and on their phone. If they are the Moonchild and that player is
              good, that player dies when night falls. Everyone who dies is offered the choice, so
              it says nothing about who the Moonchild is. If you are executed, night waits a little
              while you decide. If the Moonchild has not chosen when night falls, the app chooses a
              living player for them at random.
            </li>
          </ul>
        </div>
      </Section>

      <Section id="dying" title="When you die">
        <p>
          You are not out. Dead players keep talking, keep helping their team, and win or lose with
          it.
        </p>
        <ul>
          <li>
            Your ability stops working (unless your card says otherwise), you can&apos;t nominate,
            and you sleep through the night.
          </li>
          <li>
            You get one <strong>ghost vote</strong> for the rest of the game. Voting Yes uses it up.
            Voting No does not.
          </li>
          <li>
            You might come back. The Professor can bring back a dead Townsfolk, and the Shabaloth
            sometimes brings back a player it ate. Dawn says who is <strong>alive again</strong>,
            never why. They get their vote and their ability back, even a once-per-game one. If
            their turn that night is still to come, they wake for it, and a character that learns
            something on the first night, like the Grandmother, wakes at once to learn it again.
          </li>
          <li>
            Some players can&apos;t die at all, or not the first time: the Sailor can&apos;t die,
            and the Fool survives their first death.
          </li>
        </ul>
      </Section>

      <Section id="clues" title="Drunk and poisoned">
        <p>
          A drunk or poisoned player&apos;s ability does nothing, and anything they learn can be
          wrong. They are never told. In this game almost anyone can end up drunk:
        </p>
        <ul>
          <li>
            The <strong>Sailor</strong> makes either themselves or the player they choose drunk, and
            the <strong>Innkeeper</strong> makes one of the two players they protect drunk.
          </li>
          <li>
            The <strong>Courtier</strong> makes a character drunk for three nights and three days,
            and when a Minion is executed the <strong>Minstrel</strong> makes everyone else drunk
            until the next dusk.
          </li>
          <li>
            The first player each night to choose the <strong>Goon</strong> is made drunk, and the
            Goon joins their team.
          </li>
          <li>
            The <strong>Pukka</strong> poisons a player each night, and that player dies the next
            night.
          </li>
          <li>
            The <strong>Lunatic</strong> is told they are a Demon, usually the one in the game but
            not always. They wake and choose players to kill, but nothing happens, and the real
            Demon learns who they chose.
          </li>
        </ul>
        <p>
          A dead player has no ability, so whatever theirs was still doing ends when they die: the
          drunkenness or poison they caused, and the protection they gave. The Innkeeper&apos;s and
          the Devil&apos;s Advocate&apos;s protection also stops working while they are drunk or
          poisoned.
        </p>
        <p>
          So compare notes, look for clues that don&apos;t fit together, and don&apos;t trust
          anything completely, even your own.
        </p>
      </Section>

      <Section id="winning" title="How to win">
        <div className="card winner-good stack-sm">
          <strong className="good">Good wins</strong>
          <p>when the Demon dies, usually by being executed.</p>
        </div>
        <div className="card winner-evil stack-sm">
          <strong className="evil">Evil wins</strong>
          <p>when only two players are left alive.</p>
        </div>
        <p className="small muted">
          Two characters change this. The <strong>Zombuul</strong> survives its first death: it
          looks dead but is still alive, and still counts as alive. Execute it again to win. If the
          Demon is executed while the <strong>Mastermind</strong> is alive, the game goes on for one
          more night and day. If a good player is executed that day, evil wins. If an evil player is
          executed, or nobody is, good wins.
        </p>
        <p className="small muted">
          When the game ends, everyone sees who was who, including who was drunk or poisoned, and
          what really happened each night.
        </p>
      </Section>

      <Section id="tips" title="Tips">
        <h3 className="good">If you are good</h3>
        <ul>
          <li>
            Deaths are your clues. The Po kills no one one night, then three. The Shabaloth kills
            two at a time. The Zombuul kills only after a day when nobody died. The Pukka&apos;s
            victim dies a night late.
          </li>
          <li>
            Execute every day you can. A day without a death lets the Zombuul kill that night.
          </li>
          <li>Keep the players who protect others alive, and work out who they are protecting.</li>
        </ul>
        <h3 className="evil">If you are evil</h3>
        <ul>
          <li>
            Pretend to be a character that explains what the town sees: a survival, a death, a clue
            nobody else has.
          </li>
          <li>
            As the Demon, use what the Lunatic chooses: if you kill the same players, the town may
            take you for the Lunatic.
          </li>
        </ul>
        <EveryoneTips />
      </Section>

      <Section id="characters" title="The characters">
        <ScriptCharacters script={script} />
      </Section>
    </>
  );
}
